// ---------- startup ----------

import './css/style.css';
// En deuxième, et c'est une position et non un rangement : les modules ES sont évalués dans
// l'ordre des imports, donc poser ici les gestionnaires d'erreur les met en place AVANT
// l'évaluation de tout ce qui suit et avant la première ligne de `start()`. Pas devant la
// feuille de style, dont splash.ts lit les jetons dès son évaluation. Cf. src/report.ts.
import { bootDone } from './report';

import { initAds } from './ads';
import { applyStaticI18n } from './i18n';
import { ALPHABETS } from '../shared/alphabets';
import { wireAnim } from './anim';
import {
  wireHome,
  openHome,
  closeFacetSheet,
  backFromCreer,
  backFromDaily,
  backFromPrefs,
  backFromSheet,
  inCatalogue,
  openFromElsewhere,
  refreshPuzzles,
  closeHome,
  openHomeOn,
  openPuzzle,
} from './home';
import { demoTarget } from './demo';
import { coachReplayIntro } from './coach';
import { noteIntro } from './introStats';
import { INTRO_ID, introEnabled, introStep, openIntroPiece, restartIntro } from './intro';
import { initFullscreen, initPlatform } from './platform';
import { initPush } from './push';
import { initReview } from './review';
import { wirePrefs } from './prefs';
import { wireMaker } from './maker';
import { closeProgression, wireProgression } from './progression';
import { closeBadgeSheet, wireProfile } from './profile';
import { badgesMayHaveChanged, closeCelebration, wireCelebrate } from './celebrate';
import { closePacksPage, openPacksPage, wirePacks } from './packs';
import { wireProgressFlush } from './progress';
import { initPWA } from './pwa';
import { loadSettings } from './settings';
import { hideSplash, startSplash } from './splash';
import { bakeCells, invalidateBase, wireCodeProgress, render, invalidateWave } from './render';
import { S, measureScene } from './state';
import { wireSync } from './sync';
import {
  wireUI,
  buildLegend,
  closeOverlays,
  onCodeProgress,
  replayReveal,
  startPlay,
  afterLayout,
  setFinishedFox,
  setOnSheetFinished,
} from './ui';

function start(): void {
  // Avant toute peinture : le HTML porte encore son texte français en dur comme filet de
  // sécurité, mais le dictionnaire est la vraie source dès cette ligne (cf. src/i18n/index.ts).
  applyStaticI18n();

  // Before anything reads S.sound or animates a single cell.
  loadSettings();

  // Juste après, et avant tout le reste : le calque est déjà opaque (cf. index.html), cet
  // appel ne fait que l'animer. Il a besoin des réglages pour savoir si le joueur a coupé les
  // animations, d'où sa place ici et pas au-dessus.
  startSplash();

  const sheet = document.getElementById('sheet') as HTMLCanvasElement | null;
  if (!sheet) throw new Error('canvas #sheet missing');
  S.sheet = sheet;
  S.g = sheet.getContext('2d')!;
  // Capped on purpose, and measured: lifting it to a phone's real 2.625 buys 1.3 points of
  // sheet fill (the cell goes from 3 to 4 physical px, both floored from a scale whose
  // fractional part stays large) and costs 3.4x the time of a full frame — 4.6ms to 15.8ms
  // on a 200 grid. The blank band around the sheet is the floor in render(), not this cap.
  S.DPR = Math.min(window.devicePixelRatio || 1, 2);

  // As early as possible: the native side may fire an event (back button) right at
  // launch, and if it arrives before the listener exists it's lost — or throws in the
  // Capacitor bridge.
  // The facet sheet is home.ts's own overlay, checked first since it's what a shelf of the
  // catalogue actually has open — closeOverlays() only knows about ui.ts's.
  // L'ordre est la hiérarchie de l'écran, du plus en avant au plus en arrière : ce qui est
  // ouvert par-dessus d'abord, la feuille ensuite, et seulement si tout est déjà refermé le
  // natif minimise l'application. Sans le dernier maillon, « précédent » quittait l'app depuis
  // une partie en cours au lieu de revenir au catalogue.
  // backFromPrefs() et backFromDaily() sont les avant-derniers, et pour la même raison :
  // Réglages n'est plus un dialogue (donc plus dans closeOverlays) et le puzzle du jour n'en a
  // jamais été un — ce sont des onglets du catalogue, et « précédent » y ramène à l'étagère
  // qu'on lisait avant d'y aller. Tous deux doivent passer avant backFromSheet(), qui ne
  // regarde que si le catalogue est ouvert et consommerait le geste à leur place.
  // Ils ne peuvent pas se disputer le geste : chacun ne rend `true` que sur SON onglet.
  void initPlatform(
    () =>
      // La célébration passe devant tout (z-index 31, au-dessus même des dialogues) : retour y
      // vaut « Plus tard », et c'est elle qui se referme la première.
      closeCelebration() ||
      closeFacetSheet() ||
      closeOverlays() ||
      // Le détail d'un badge s'ouvre par-dessus le trophée : il se referme avant lui.
      closeBadgeSheet() ||
      closeProgression() ||
      closePacksPage() ||
      // L'outil de création, même rang que la page Packs : il ne s'ouvre que depuis la
      // Bibliothèque, et « précédent » y remonte d'une étape avant de le refermer.
      backFromCreer() ||
      backFromPrefs() ||
      backFromDaily() ||
      backFromSheet(),
  );
  // Its web counterpart: arms the full screen an older installed icon didn't get.
  initFullscreen();
  // Câble le listener de token AVANT tout appel à enablePush() (Réglages) : PushNotifications.
  // register() ne sert à rien si personne n'écoute encore 'registration' quand il répond.
  initPush();

  // The animation engine doesn't know about layers: it calls these three callbacks.
  wireAnim({ frame: render, bake: bakeCells, wave: invalidateWave, reveal: replayReveal });
  wireCodeProgress(onCodeProgress);

  measureScene();
  wireUI();
  buildLegend();
  // `refreshPuzzles` passé en argument : le dialogue du code de reprise doit recharger le
  // catalogue après une reprise (la liste dépend du compte), et home.ts importe déjà prefs.ts —
  // l'injection évite le cycle. Cf. reloadCatalogue dans prefs.ts.
  wirePrefs(refreshPuzzles); // before wireHome: its tab bar opens the dialog
  wireMaker();
  wireHome();
  wireProgression(openFromElsewhere, inCatalogue, openPacksPage);
  wireProfile();
  // Après wireProfile : la célébration s'abonne aux réponses fraîches de l'écran « Moi ».
  wireCelebrate();
  wirePacks();
  wireProgressFlush();
  // After wireProgressFlush, and that order is load-bearing: both listen for the app being
  // backgrounded, and the pending save has to reach the store before the sync looks for
  // something to push.
  wireSync(badgesMayHaveChanged);

  // Native only, and deliberately after the first measure: the banner reports its height
  // asynchronously, and afterLayout() re-measures the sheet around whatever it takes.
  void initAds(afterLayout);

  // Rien ne s'affiche ici : on note la date de première ouverture et, le cas échéant, qu'une
  // mise à jour vient d'arriver. Les deux doivent être écrits bien avant que la première
  // question ne se pose — cf. src/review.ts.
  initReview();

  // Its counterpart on the web: the service worker that makes the page installable and
  // playable offline. No-op in the APK.
  initPWA();

  // La feuille de repli, pour qu'il y ait toujours quelque chose à peindre même si le
  // catalogue est injoignable : une grille figée, livrée avec l'app (cf. src/demo.ts). Le jeu
  // ne convertit plus d'image — c'est le métier de l'admin.
  const demo = demoTarget();
  S.alphabetId = demo.alphabetId;
  S.gridW = demo.gridW;
  S.gridH = demo.gridH;
  S.target = demo.target;
  startPlay();
  openHome();
  // La première feuille d'un nouveau joueur : l'œil du chien, ouvert d'office au lieu du
  // catalogue, et retrouvé tel quel s'il a fermé au milieu (cf. src/intro.ts). Pas sous une
  // feuille d'essai, qui a sa propre adresse et ne doit pas se faire passer devant.
  // « Voir la bibliothèque », au bout de l'œil : le catalogue, sur l'onglet où sont les packs.
  const toLibrary = (): void => openHomeOn('library');
  // Asynchrone : la décision lit le jeton du compte (cf. introStep), quelques millisecondes sous
  // l'écran de lancement, qui tient de toute façon sa durée minimale.
  if (!new URLSearchParams(location.search).has('essai'))
    void introStep().then((intro) => {
      if (intro === 'piece' && openIntroPiece(toLibrary)) closeHome();
      else if (intro === 'big')
        void openPuzzle(INTRO_ID, innerWidth / 2, innerHeight / 2).catch(() => {});
    });
  // Le dernier mot du renard sur la carte « Terminé » du chien, et son chemin vers les autres.
  setFinishedFox(INTRO_ID);
  document.getElementById('finishedFoxGo')!.onclick = toLibrary;
  // L'entonnoir de l'initiation : la dernière étape, le chien entier fini (cf. introStats.ts).
  setOnSheetFinished((id) => {
    if (id === INTRO_ID) noteIntro('bigDone');
  });
  // « Revoir l'initiation », dans les réglages : ici et pas dans prefs.ts, qui ne peut importer
  // ni l'initiation ni le catalogue sans fermer un cycle (prefs ← ui ← piece ← intro).
  const replay = document.getElementById('prefIntro')!;
  replay.hidden = !introEnabled();
  replay.onclick = () => {
    if (!restartIntro(toLibrary)) return;
    closeHome();
    coachReplayIntro();
  };
  // Une image de sources/ convertie à la volée, pour juger un alphabet en jouant (cf. essai.ts).
  // Import dynamique derrière DEV : ni le module ni la conversion n'entrent dans le build.
  if (import.meta.env.DEV) void import('./essai').then((m) => m.openTrialFromUrl());
  // Le catalogue est monté ; il sera peint à la prochaine image, et c'est à ce moment-là que le
  // calque n'a plus rien à couvrir. hideSplash() tient lui-même sa durée minimale, donc rien
  // ici n'attend quoi que ce soit.
  requestAnimationFrame(() => hideSplash());

  // Fonts arrive after the first render: the advance measured on the fallback font is wrong,
  // and it is the cell's own ratio in the monospace alphabets. We re-measure and redraw; the
  // GRID doesn't move for all that — its codes came from the server, only their drawing did.
  // Le démarrage est allé au bout : à partir d'ici, une erreur se journalise en silence au lieu
  // de poser l'écran de secours (cf. src/report.ts). Avant la mesure des polices, qui est
  // asynchrone et n'appartient plus au démarrage.
  bootDone();

  void document.fonts?.ready.then(() => {
    let redo = false;
    for (const a of ALPHABETS) {
      if (a.remeasure) {
        a.remeasure();
        redo = redo || a.id === S.alphabetId;
      }
    }
    buildLegend();
    if (redo) {
      measureScene();
      invalidateBase();
      render();
    }
  });
}

start();
